import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Search, CheckCircle2, XCircle } from "lucide-react";

export default function ClientQueue() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (search) params.search = search;
      if (status) params.kyc_status = status;
      const { data } = await adminApi.get("/admin/clients", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const approve = async (id) => {
    try {
      const { data } = await adminApi.post(`/admin/clients/${id}/approve`, {});
      const b = data?.bse2_ucc || {};
      if (b.status === "SUBMITTED") toast.success(`Approved — UCC ${b.ucc || ""} sent to BSE; investor will get the approval email`);
      else if (b.status) toast.warning(`BSE UCC was not completed: ${b.message || b.status}`);
      else toast.success(`Client review status: ${data.status}`);
      load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const reject = async (id) => {
    try { await adminApi.post(`/admin/clients/${id}/reject`, {}); toast.success("Client rejected"); load(); }
    catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <AdminLayout>
      <h1 className="mb-6 font-heading text-3xl text-[var(--brand)]">Clients</h1>
      <Card className="mb-4 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-muted)]" />
            <Input className="pl-9" placeholder="Search by name, email, client code..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-client-search-input" />
          </div>
          <Select value={status || "__all"} onValueChange={(value) => setStatus(value === "__all" ? "" : value)}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-client-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All statuses</SelectItem>
              <SelectItem value="PENDING_REVIEW">Pending Review</SelectItem>
              <SelectItem value="PENDING_BSE_AUTHENTICATION">Pending BSE Authentication</SelectItem>
              <SelectItem value="PENDING_BSE_RECONCILIATION">Pending BSE Reconciliation</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="NOT_STARTED">Not Started</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
            data.items.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="admin-no-clients-message">No clients found.</div> :
            <div className="divide-y divide-[var(--glass-border)]">
              {data.items.map((c) => (
                <div key={c.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-client-row-${c.id}`}>
                  <div>
                    <Link to={`/admin/clients/${c.id}`} className="font-medium text-[var(--brand)] hover:underline" data-testid={`admin-client-link-${c.id}`}>{c.full_name}</Link>
                    <div className="text-xs text-[var(--ink-muted)]">{c.email} &middot; {c.client_code || "No client code"} &middot; Joined {formatDate(c.created_at)}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={c.kyc_status} />
                    {c.kyc_status === "PENDING_REVIEW" && (
                      <>
                        <Button size="sm" className="rounded-full bg-[var(--cta)]" onClick={() => approve(c.id)} data-testid={`admin-approve-${c.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => reject(c.id)} data-testid={`admin-reject-${c.id}`}><XCircle className="mr-1 h-3.5 w-3.5" />Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
